import dayjs from 'dayjs';
import React, { ReactNode } from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetErrorBoundary,
}) => {
  const [showDetails, setShowDetails] = React.useState(false);
  const [errorId] = React.useState(
    () =>
      'err_' +
      Math.random().toString(36).substr(2, 9) +
      '_' +
      dayjs().valueOf(),
  );

  const handleGoHome = () => {
    window.location.href = '/';
  };

  const handleCopyError = () => {
    const errorInfo = {
      errorId,
      timestamp: dayjs().toISOString(),
      message: error.message,
      stack: error.stack,
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    navigator.clipboard
      .writeText(JSON.stringify(errorInfo, null, 2))
      .then(() => alert('錯誤信息已複製到剪貼板'))
      .catch(() => console.error('無法複製錯誤信息'));
  };

  return (
    <div>
      <div>
        <div className="mb-4">
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>

        <h1>系統發生錯誤</h1>

        <p>很抱歉，應用程式遇到了意外錯誤。請嘗試重新載入頁面。</p>

        <div>
          <div>
            錯誤 ID: <code>{errorId}</code>
          </div>
          <div>時間: {dayjs().format('YYYY-MM-DD HH:mm:ss')}</div>
        </div>

        <div>
          <button onClick={resetErrorBoundary}>重試</button>

          <button onClick={handleGoHome}>回到首頁</button>

          <div>
            <button onClick={() => setShowDetails(!showDetails)}>
              {showDetails ? '隱藏' : '顯示'}錯誤詳情
            </button>

            <button onClick={handleCopyError}>複製錯誤信息</button>
          </div>
        </div>

        {showDetails && (
          <div>
            <div>
              <h4>錯誤詳細信息</h4>

              <div>
                <div>
                  <span>錯誤類型:</span>
                  <span>{error.name}</span>
                </div>

                <div>
                  <span>錯誤消息:</span>
                  <div>{error.message}</div>
                </div>

                <div>
                  <span>網址:</span>
                  <div>{window.location.href}</div>
                </div>

                <div>
                  <span>瀏覽器:</span>
                  <div>{navigator.userAgent}</div>
                </div>

                {error.stack && (
                  <div>
                    <span>Stack Trace:</span>
                    <div>
                      <pre>{error.stack}</pre>
                    </div>
                  </div>
                )}

                <div>
                  提示：您可以複製上述信息並傳送給開發團隊以協助解決問題。
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface ErrorBoundaryProps {
  children: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  fallback?: React.ComponentType<ErrorFallbackProps>;
}

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  children,
  onError,
  fallback: CustomFallback,
}) => {
  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    const errorId =
      'err_' +
      Math.random().toString(36).substr(2, 9) +
      '_' +
      dayjs().valueOf();

    console.group('🚨 React ErrorBoundary 捕獲到錯誤');
    console.error('錯誤 ID:', errorId);
    console.error('錯誤物件:', error);
    console.error('錯誤信息:', errorInfo);
    console.groupEnd();

    const errorData = {
      errorId,
      name: error.name,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack || 'Unknown',
      timestamp: dayjs().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      props: errorInfo.componentStack
        ? errorInfo.componentStack.split('\n')[1]?.trim()
        : 'Unknown component',
    };

    // Log error to console for debugging
    console.error('React Error Boundary:', errorData);

    if (onError) {
      onError(error, errorInfo);
    }
  };

  return (
    <ReactErrorBoundary
      FallbackComponent={CustomFallback || ErrorFallback}
      onError={handleError}
      onReset={() => window.location.reload()}
    >
      {children}
    </ReactErrorBoundary>
  );
};

export default ErrorBoundary;
